import {
  EXPORT_MAX_ROWS,
  UNIT_STATUS_LABELS,
  UNIT_TYPE_LABELS,
  UnitCommercialStatus,
  UnitType,
  type UnitDto,
} from '@performance-crm/shared';
import { Ban, Check, Download, Loader2, Paperclip, Pencil, Plus } from 'lucide-react';
import { useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { AttachmentsDialog } from '@/components/attachments/AttachmentsDialog';
import { ListFooter } from '@/components/commercial/ListFooter';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/toast-context';
import { fmt, todayInTz } from '@/lib/format';
import {
  fetchAllPages,
  uniqueById,
  useBlockUnit,
  useCreateUnit,
  useProjects,
  useStockSummary,
  useUnitsInfinite,
  useUpdateUnit,
  type UnitListParams,
} from '@/lib/queries';
import { downloadWorkbook } from '@/lib/spreadsheet';
import { useCompanyTimezone } from '@/lib/useCompanyTimezone';
import { useDebounced } from '@/lib/useDebounced';
import { stockSheet } from './commercialExport';

const STATUS_TONE: Record<UnitCommercialStatus, 'success' | 'warning' | 'muted' | 'danger'> = {
  DISPONIBLE: 'success',
  SEPARADO: 'warning',
  VENDIDO: 'muted',
  BLOQUEADO: 'danger',
};

/** Stock del proyecto: filtros, «Mostrando 200 de N» con «Cargar más», Excel y edición / bloqueo por unidad. */
export function StockTab({
  companyId,
  projectId,
  canManage,
}: {
  companyId: string;
  projectId: string;
  canManage: boolean;
}) {
  const tz = useCompanyTimezone(companyId);
  const { data: projects } = useProjects();
  const toast = useToast();
  const [exporting, setExporting] = useState(false);
  const [status, setStatus] = useState('');
  const [unitType, setUnitType] = useState('');
  const [q, setQ] = useState('');
  const [bedrooms, setBedrooms] = useState('');
  const [areaMin, setAreaMin] = useState('');
  const [areaMax, setAreaMax] = useState('');
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');
  const hasRanges = !!(bedrooms || areaMin || areaMax || priceMin || priceMax);
  const clearRanges = () => {
    setBedrooms('');
    setAreaMin('');
    setAreaMax('');
    setPriceMin('');
    setPriceMax('');
  };
  const needle = useDebounced(q.trim(), 300);
  const params: UnitListParams = {
    projectId,
    status,
    unitType,
    q: needle || undefined,
    bedrooms,
    areaMin,
    areaMax,
    priceMin,
    priceMax,
  };
  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useUnitsInfinite(params);
  // Sin repetidos: el cursor del stock es por desplazamiento y una unidad creada o movida entre dos páginas
  // podría repetirse.
  const items = useMemo(() => uniqueById(data?.pages.flatMap((p) => p.items) ?? []), [data]);
  const total = data?.pages[0]?.total;
  const { data: summary } = useStockSummary(projectId);
  const exportExcel = async () => {
    setExporting(true);
    try {
      const all = await fetchAllPages<UnitDto>('/api/v1/units', params);
      const project = projects?.find((p) => p.id === projectId)?.name ?? 'proyecto';
      const slug = project
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      await downloadWorkbook([stockSheet(all.items, tz)], `stock-${slug}-${todayInTz(tz)}.xlsx`);
      if (all.truncated)
        toast.error(
          'Excel incompleto',
          `Se exportaron las primeras ${fmt.number(EXPORT_MAX_ROWS)} unidades: afina los filtros para el resto.`,
        );
    } catch (err) {
      toast.error('No se pudo generar el Excel', (err as Error).message);
    } finally {
      setExporting(false);
    }
  };
  const [editing, setEditing] = useState<UnitDto | 'new' | null>(null);
  const [blocking, setBlocking] = useState<UnitDto | null>(null);
  const [files, setFiles] = useState<UnitDto | null>(null);
  return (
    <div className="space-y-4">
      {summary && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5" data-testid="stock-summary">
          {(
            [
              ['Total', summary.total, 'default'],
              ['Disponibles', summary.byStatus.DISPONIBLE, 'success'],
              ['Separadas', summary.byStatus.SEPARADO, 'warning'],
              ['Vendidas', summary.byStatus.VENDIDO, 'muted'],
              ['Bloqueadas', summary.byStatus.BLOQUEADO, 'danger'],
            ] as const
          ).map(([label, n, tone]) => (
            <Card key={label}>
              <CardContent className="p-4">
                <p className="text-muted-foreground text-xs uppercase">{label}</p>
                <p
                  className={`text-2xl font-semibold ${tone === 'success' ? 'text-success' : tone === 'danger' ? 'text-danger' : tone === 'warning' ? 'text-warning' : ''}`}
                >
                  {n}
                </p>
              </CardContent>
            </Card>
          ))}
          <p className="text-muted-foreground text-xs sm:col-span-2 lg:col-span-5">
            {summary.byType
              .map(
                (t) =>
                  `${UNIT_TYPE_LABELS[t.unitType] ?? t.unitType}: ${t.disponibles}/${t.total} disponibles`,
              )
              .join(' · ')}
            {summary.availableByCurrency.length
              ? ` · valor disponible ${summary.availableByCurrency.map((v) => fmt.money(v.amount, v.currency)).join(' + ')}`
              : ''}
            {summary.availableEstimated
              ? ` (≈ ${fmt.money(summary.availableEstimated.amount, summary.availableEstimated.currency)} estimado al tipo de cambio ${summary.availableEstimated.penPerUsd})`
              : ''}
          </p>
        </div>
      )}
      <div className="bg-card flex flex-wrap items-center gap-2 rounded-lg border p-3">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Código o tipología…"
          aria-label="Buscar unidad"
          className="h-9 w-44"
        />
        <Select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          aria-label="Estado"
          className="h-9 w-44"
        >
          <option value="">Todos los estados</option>
          {Object.values(UnitCommercialStatus).map((s) => (
            <option key={s} value={s}>
              {UNIT_STATUS_LABELS[s]}
            </option>
          ))}
        </Select>
        <Select
          value={unitType}
          onChange={(e) => setUnitType(e.target.value)}
          aria-label="Tipo"
          className="h-9 w-44"
        >
          <option value="">Todos los tipos</option>
          {Object.values(UnitType).map((t) => (
            <option key={t} value={t}>
              {UNIT_TYPE_LABELS[t]}
            </option>
          ))}
        </Select>
        <Select
          value={bedrooms}
          onChange={(e) => setBedrooms(e.target.value)}
          aria-label="Dormitorios"
          className="h-9 w-44"
        >
          <option value="">Dormitorios: todos</option>
          {(summary?.bedrooms ?? []).map((n) => (
            <option key={n} value={String(n)}>
              {n} dormitorio{n === 1 ? '' : 's'}
            </option>
          ))}
        </Select>
        <Input
          type="number"
          min={0}
          step="0.01"
          value={areaMin}
          onChange={(e) => setAreaMin(e.target.value)}
          placeholder="m² desde"
          aria-label="Área desde"
          className="h-9 w-28"
        />
        <Input
          type="number"
          min={0}
          step="0.01"
          value={areaMax}
          onChange={(e) => setAreaMax(e.target.value)}
          placeholder="m² hasta"
          aria-label="Área hasta"
          className="h-9 w-28"
        />
        <Input
          type="number"
          min={0}
          value={priceMin}
          onChange={(e) => setPriceMin(e.target.value)}
          placeholder="Precio desde"
          aria-label="Precio desde"
          className="h-9 w-32"
        />
        <Input
          type="number"
          min={0}
          value={priceMax}
          onChange={(e) => setPriceMax(e.target.value)}
          placeholder="Precio hasta"
          aria-label="Precio hasta"
          className="h-9 w-32"
        />
        {hasRanges && (
          <Button type="button" variant="ghost" size="sm" onClick={clearRanges}>
            Limpiar filtros
          </Button>
        )}
        <div className="ml-auto flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => void exportExcel()}
            disabled={!data || items.length === 0 || exporting}
            data-testid="export-stock"
          >
            {exporting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}{' '}
            Exportar Excel
          </Button>
          {canManage && (
            <>
              <Button asChild size="sm" variant="outline">
                <Link to={`/admin?tab=unidades&projectId=${projectId}`}>Importar Excel / CSV</Link>
              </Button>
              <Button size="sm" onClick={() => setEditing('new')} data-testid="btn-new-unit">
                <Plus className="h-4 w-4" /> Nueva unidad
              </Button>
            </>
          )}
        </div>
      </div>
      {isLoading && <Skeleton className="h-64 w-full" />}
      {data && (
        <div className="bg-card overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-muted-foreground text-left text-xs uppercase tracking-wide">
              <tr>
                <th className="px-3 py-2">Unidad</th>
                <th className="px-3 py-2">Tipo</th>
                <th className="px-3 py-2">Tipología</th>
                <th className="px-2 py-2 text-right">Área</th>
                <th className="px-2 py-2 text-right">Precio</th>
                <th className="px-3 py-2">Estado</th>
                <th className="px-3 py-2">Cliente</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.id} className="border-t" data-testid="unit-row">
                  <td className="px-3 py-2">
                    <p className="font-medium">{u.code}</p>
                    <p className="text-muted-foreground text-xs">
                      {u.subdivisionName ?? ''}
                      {u.floor !== null ? ` · piso ${u.floor}` : ''}
                    </p>
                  </td>
                  <td className="px-3 py-2">{UNIT_TYPE_LABELS[u.unitType] ?? u.unitType}</td>
                  <td className="px-3 py-2">
                    {u.typology ?? '—'}
                    {u.bedrooms !== null ? ` · ${u.bedrooms}D` : ''}
                  </td>
                  <td className="px-2 py-2 text-right">
                    {u.areaTotal ? `${Number(u.areaTotal)} m²` : '—'}
                  </td>
                  <td className="px-2 py-2 text-right">
                    {fmt.money(u.priceSale ?? u.priceList, u.currency)}
                    {u.priceList && u.priceSale && Number(u.priceList) !== Number(u.priceSale) && (
                      <span className="text-muted-foreground block text-xs">
                        lista {fmt.money(u.priceList, u.currency)}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <Badge tone={STATUS_TONE[u.commercialStatus]}>
                      {UNIT_STATUS_LABELS[u.commercialStatus]}
                    </Badge>
                    {u.isBlacklisted && u.blockReason && (
                      <p className="text-muted-foreground mt-0.5 text-xs">{u.blockReason}</p>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {u.holder ? (
                      u.holder.leadId && u.holder.contactName ? (
                        <Link
                          to={`/leads/${u.holder.leadId}`}
                          className="text-primary hover:underline"
                        >
                          {u.holder.contactName}
                        </Link>
                      ) : (
                        // Cliente de otro asesor (P17): solo el tipo de proceso y quién lo atiende.
                        <span className="text-muted-foreground" data-testid="unit-holder-hidden">
                          {u.holder.dealType === 'VENTA' ? 'Vendida' : 'Separada'}
                          {u.holder.advisorName ? ` · ${u.holder.advisorName}` : ''}
                        </span>
                      )
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        aria-label="Archivos"
                        onClick={() => setFiles(u)}
                        data-testid="btn-unit-files"
                      >
                        <Paperclip className="h-4 w-4" />
                      </Button>
                      {canManage && (
                        <>
                          <Button
                            size="icon"
                            variant="ghost"
                            aria-label="Editar"
                            onClick={() => setEditing(u)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          {u.commercialStatus === 'DISPONIBLE' ||
                          u.commercialStatus === 'BLOQUEADO' ? (
                            <Button
                              size="icon"
                              variant="ghost"
                              aria-label={u.isBlacklisted ? 'Liberar' : 'Bloquear'}
                              className={u.isBlacklisted ? 'text-success' : 'text-destructive'}
                              onClick={() => setBlocking(u)}
                              data-testid="btn-block"
                            >
                              {u.isBlacklisted ? (
                                <Check className="h-4 w-4" />
                              ) : (
                                <Ban className="h-4 w-4" />
                              )}
                            </Button>
                          ) : null}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-muted-foreground px-3 py-8 text-center">
                    Sin unidades con estos filtros.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      <ListFooter
        shown={items.length}
        total={total}
        noun="unidades"
        hasMore={!!hasNextPage}
        loading={isFetchingNextPage}
        onMore={() => void fetchNextPage()}
        testId="stock-footer"
      />
      {editing && (
        <UnitDialog
          projectId={projectId}
          unit={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
      {blocking && <BlockDialog unit={blocking} onClose={() => setBlocking(null)} />}
      {files && (
        <AttachmentsDialog
          target={{ entityType: 'UNIT', entityId: files.id, label: `Unidad ${files.code}` }}
          canUpload={canManage}
          onClose={() => setFiles(null)}
        />
      )}
    </div>
  );
}

function UnitDialog({
  projectId,
  unit,
  onClose,
}: {
  projectId: string;
  unit: UnitDto | null;
  onClose: () => void;
}) {
  const create = useCreateUnit();
  const update = useUpdateUnit();
  const toast = useToast();
  const [f, setF] = useState({
    code: unit?.code ?? '',
    unitType: (unit?.unitType ?? 'DEPARTAMENTO') as UnitType,
    typology: unit?.typology ?? '',
    floor: unit?.floor?.toString() ?? '',
    bedrooms: unit?.bedrooms?.toString() ?? '',
    bathrooms: unit?.bathrooms ?? '',
    areaTotal: unit?.areaTotal ?? '',
    priceList: unit?.priceList ?? '',
    priceSale: unit?.priceSale ?? '',
    currency: unit?.currency ?? 'USD',
    view: unit?.view ?? '',
  });
  const set = (patch: Partial<typeof f>) => setF((p) => ({ ...p, ...patch }));
  const num = (v: string) => (v === '' ? null : Number(v));
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const payload = {
      code: f.code,
      unitType: f.unitType,
      typology: f.typology || null,
      floor: num(f.floor),
      bedrooms: num(f.bedrooms),
      bathrooms: num(f.bathrooms),
      areaTotal: num(f.areaTotal),
      priceList: num(f.priceList),
      priceSale: num(f.priceSale),
      currency: f.currency as 'USD' | 'PEN',
      view: f.view || null,
    };
    try {
      if (unit) await update.mutateAsync({ id: unit.id, input: payload });
      else await create.mutateAsync({ ...payload, projectId });
      toast.success(unit ? 'Unidad actualizada' : 'Unidad creada');
      onClose();
    } catch (err) {
      toast.error('No se pudo guardar', (err as Error).message);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>{unit ? `Editar ${unit.code}` : 'Nueva unidad'}</DialogTitle>
            <DialogDescription>Los cambios de precio y datos quedan auditados.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1">
              <Label htmlFor="u-code">Código</Label>
              <Input
                id="u-code"
                value={f.code}
                onChange={(e) => set({ code: e.target.value })}
                required
                disabled={!!unit}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="u-type">Tipo</Label>
              <Select
                id="u-type"
                value={f.unitType}
                onChange={(e) => set({ unitType: e.target.value as UnitType })}
              >
                {Object.values(UnitType).map((t) => (
                  <option key={t} value={t}>
                    {UNIT_TYPE_LABELS[t]}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="u-typ">Tipología</Label>
              <Input
                id="u-typ"
                value={f.typology}
                onChange={(e) => set({ typology: e.target.value })}
                placeholder="2D+E"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="u-floor">Piso</Label>
              <Input
                id="u-floor"
                type="number"
                value={f.floor}
                onChange={(e) => set({ floor: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="u-bed">Dormitorios</Label>
              <Input
                id="u-bed"
                type="number"
                min={0}
                value={f.bedrooms}
                onChange={(e) => set({ bedrooms: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="u-bath">Baños</Label>
              <Input
                id="u-bath"
                type="number"
                min={0}
                step="0.5"
                value={f.bathrooms}
                onChange={(e) => set({ bathrooms: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="u-area">Área total (m²)</Label>
              <Input
                id="u-area"
                type="number"
                min={0}
                step="0.01"
                value={f.areaTotal}
                onChange={(e) => set({ areaTotal: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="u-list">Precio de lista</Label>
              <Input
                id="u-list"
                type="number"
                min={0}
                value={f.priceList}
                onChange={(e) => set({ priceList: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="u-sale">Precio de venta</Label>
              <Input
                id="u-sale"
                type="number"
                min={0}
                value={f.priceSale}
                onChange={(e) => set({ priceSale: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="u-cur">Moneda</Label>
              <Select
                id="u-cur"
                value={f.currency}
                onChange={(e) => set({ currency: e.target.value as 'USD' | 'PEN' })}
              >
                <option value="USD">US$</option>
                <option value="PEN">S/</option>
              </Select>
            </div>
            <div className="space-y-1 sm:col-span-2">
              <Label htmlFor="u-view">Vista</Label>
              <Input id="u-view" value={f.view} onChange={(e) => set({ view: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={create.isPending || update.isPending}>
              Guardar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function BlockDialog({ unit, onClose }: { unit: UnitDto; onClose: () => void }) {
  const block = useBlockUnit();
  const toast = useToast();
  const [reason, setReason] = useState(unit.blockReason ?? '');
  const blocking = !unit.isBlacklisted;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await block.mutateAsync({
        id: unit.id,
        input: { blocked: blocking, reason: reason || null },
      });
      toast.success(blocking ? `${unit.code} bloqueada` : `${unit.code} liberada`);
      onClose();
    } catch (err) {
      toast.error('No se pudo actualizar', (err as Error).message);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>{blocking ? `Bloquear ${unit.code}` : `Liberar ${unit.code}`}</DialogTitle>
            <DialogDescription>
              {blocking
                ? 'Una unidad en blacklist no se puede proformar ni separar (piloto, reservada por la inmobiliaria, etc.).'
                : 'La unidad vuelve a estar disponible para proformas y separaciones.'}
            </DialogDescription>
          </DialogHeader>
          {blocking && (
            <div className="space-y-1">
              <Label htmlFor="b-reason">Motivo</Label>
              <Input
                id="b-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                required
                autoFocus
              />
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant={blocking ? 'destructive' : 'default'}
              disabled={block.isPending}
              data-testid="block-submit"
            >
              {blocking ? 'Bloquear' : 'Liberar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
